import { Component, Show, createSignal } from 'solid-js';
import { TbChevronRight, TbChevronDown, TbWorld } from 'solid-icons/tb';
import RealtimeClicksPanel from '../panels/RealtimeClicksPanel';
import TopLeadsCountry from '../panels/TopLeadsCountry';

interface RightSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  shouldMinimizeRealtimeClicks?: boolean;
  savedRealtimeClicksState?: () => boolean;
  onRealtimeClicksStateChange?: (expanded: boolean) => void;
}

/**
 * Right Sidebar Component
 * Fixed positioned sidebar showing real-time clicks and leads by countries
 * Stays visible when scrolling, main content shrinks via margin-right when expanded
 */
const RightSidebar: Component<RightSidebarProps> = (props) => {
  const [leadsOpen, setLeadsOpen] = createSignal(true);
  const [realtimeClicksExpanded, setRealtimeClicksExpanded] = createSignal(true);

  // Track expansion state and notify parent
  const handleRealtimeClicksStateChange = (expanded: boolean) => {
    setRealtimeClicksExpanded(expanded);
    if (props.onRealtimeClicksStateChange) {
      props.onRealtimeClicksStateChange(expanded);
    }
  };

  return (
    <div
      class={`hidden lg:flex flex-col fixed right-0 top-0 h-screen z-20
         bg-body dark:bg-dark border-l border-dashed border-gray-200
         dark:border-gray-700 shadow-light dark:shadow-none transition-all
         duration-300 overflow-hidden ${props.isOpen ? 'w-80' : 'w-0'
        }`}
    >
      <Show when={props.isOpen}>
        {/* Real-time Clicks - Top/Flexible Section */}
        <div class={`overflow-hidden transition-all duration-600 brand-scrollbar border-t border-dashed border-gray-200 dark:border-gray-700 ${leadsOpen() ? 'max-h-[50vh]' : 'flex-1 min-h-0'
          }`}>
          <RealtimeClicksPanel
            maxClicks={50}
            shouldMinimize={props.shouldMinimizeRealtimeClicks}
            savedExpandedState={props.savedRealtimeClicksState}
          />
        </div>

        {/* Top Leads Country - Bottom Section (Collapsible) */}
        <Show when={leadsOpen()}>
          <div class="flex flex-col border-t border-dashed border-gray-200 dark:border-gray-700 overflow-hidden flex-1 min-h-0">
            <TopLeadsCountry isOpen={leadsOpen()} onToggle={() => setLeadsOpen(!leadsOpen())} />
          </div>
        </Show>

        {/* Collapsed Leads Header - Show when minimized */}
        <Show when={!leadsOpen()}>
          <div class="h-12 flex items-center justify-between px-4 border-t border-dashed border-gray-200 dark:border-gray-700 bg-body dark:bg-dark hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
            <h2 class="text-lg font-medium uppercase text-gray-900 dark:text-white flex items-center gap-2 flex-1 tracking-wider">
              <TbWorld size={20} class="text-orange-500" />
              <span>Leads by Countries</span>
            </h2>
            <button
              onClick={() => setLeadsOpen(!leadsOpen())}
              class="flex items-center justify-center p-1 text-gray-700 dark:text-gray-100 hover:text-orange-500 dark:hover:text-orange-400 transition-colors flex-shrink-0 font-body"
              title="Expand leads by countries"
            >
              <TbChevronDown size={20} class="rotate-180" />
            </button>
          </div>
        </Show>
      </Show>
    </div>
  );
};

export default RightSidebar;
